import type { I18n } from 'vue-i18n';
import { createI18n } from 'vue-i18n';
import type { UserModule } from '../types';
import shapthParameterLocales from '../data/shapth-parameter-locales.json';

interface Locales {
    [index: string]: Locales | string;
}

const globalLocales = import.meta.glob<{ default: Locales }>(
    '../locales/*.json',
    { eager: true }
);
const globalLocalePaths = Object.keys(globalLocales);
const messages: { [index: string]: Locales } = {};

for (let i = 0; i < globalLocalePaths.length; i++) {
    const globalPath = globalLocalePaths[i];
    const [, lang] = globalPath.match(/^.*\/([a-z]{2})\.json$/) || [];
    if (lang) {
        messages[lang] = globalLocales[globalPath].default || {};
    }
}
// merged here instead of per-component (mergeLocaleMessage does not trigger
// re-renders in vue-i18n 11.4.x)
const deLaboratory = (messages.de.laboratory ?? {}) as Locales;
messages.de.laboratory = {
    ...deLaboratory,
    params: shapthParameterLocales
};
export const i18n: I18n = createI18n({
    legacy: false,
    locale: 'de',
    fallbackLocale: 'de',
    warnHtmlMessage: false,
    datetimeFormats: {
        de: {
            short: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            },
            long: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                weekday: 'short',
                hour: 'numeric',
                minute: 'numeric'
            }
        }
    },
    numberFormats: {
        de: {
            currency: {
                style: 'currency',
                currency: 'EUR', // e.g. 'USD', 'EUR'
                currencyDisplay: 'symbol', // 'symbol' (e.g. '$') or 'code' (e.g. 'USD')
                useGrouping: true
            }
        }
    },
    messages
});

export function getCurrentLocale(): string {
    return typeof i18n.global.locale === 'string'
        ? i18n.global.locale
        : i18n.global.locale.value;
}

export interface CurrencyInfo {
    locale: string;
    currency: string;
    currencyDisplay: string;
    currencySymbol: string;
    useGrouping: boolean;
}

const fallbackCurrencyInfo: CurrencyInfo = {
    locale: '',
    currency: 'EUR',
    currencyDisplay: 'code',
    currencySymbol: '',
    useGrouping: false
};

export function getCurrencyInfo(locale: string): CurrencyInfo {
    const numberFormats = (
        i18n.global.getNumberFormat(locale) as Record<
            string,
            Intl.NumberFormatOptions
        >
    )['currency'];
    if (numberFormats) {
        return {
            locale: locale,
            currency: numberFormats.currency ?? fallbackCurrencyInfo.currency,
            currencyDisplay:
                numberFormats.currencyDisplay ??
                fallbackCurrencyInfo.currencyDisplay,
            currencySymbol:
                getCurrencySymbol(
                    numberFormats.currency ?? fallbackCurrencyInfo.currency,
                    locale,
                    numberFormats.currencyDisplay ??
                        fallbackCurrencyInfo.currencyDisplay
                ) ?? fallbackCurrencyInfo.currencySymbol,
            useGrouping:
                numberFormats.useGrouping === true ||
                numberFormats.useGrouping === 'true' ||
                fallbackCurrencyInfo.useGrouping
        };
    } else {
        // fallback if no currency formatting defined for given locale
        console.error(`No currency formatting defined for locale ${locale}`);
        return {
            locale: locale,
            currency: fallbackCurrencyInfo.currency,
            currencyDisplay: fallbackCurrencyInfo.currencyDisplay,
            currencySymbol:
                getCurrencySymbol(
                    fallbackCurrencyInfo.currency,
                    locale,
                    fallbackCurrencyInfo.currencyDisplay
                ) ?? fallbackCurrencyInfo.currencySymbol,
            useGrouping: fallbackCurrencyInfo.useGrouping
        };
    }
}

function getCurrencySymbol(
    currency: string,
    locale: string,
    symbolOrCode: string
): string | undefined {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        currencyDisplay: symbolOrCode
    } as Intl.NumberFormatOptions)
        .formatToParts(1)
        .find((x) => x.type === 'currency')?.value;
}

export const install: UserModule = ({ app }) => {
    app.use(i18n);
};
